import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { StockRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayDate, formatQty, parseApiTime } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Input, Select, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hq } from '../api/client';
import { MoneyText } from '../components/Money';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

/** 库存查询：只读现存量（wl_stock），点击行看流水。 */
export default function StockList() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [search, setSearch] = useState('');
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [warehouseId, setWarehouseId] = useState('');

  const warehouses = useQuery({
    queryKey: ['org-structure', 'warehouse', 'options'],
    queryFn: () => hq.orgStructure.warehouse.list(),
    staleTime: 5 * 60 * 1000
  });

  const query = useInfiniteQuery({
    queryKey: ['stock', 'list', search, onlyAvailable, warehouseId],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.stock.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        keyword: search || undefined,
        warehouseId: warehouseId || undefined,
        onlyAvailable
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<StockRow> = [
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '规格', dataIndex: 'spec', width: 130, render: (value: string | null) => value || '-' },
    { title: '批次', dataIndex: 'batchNo', width: 120, render: (value: string | null) => value || '-' },
    {
      title: '合计',
      dataIndex: 'totalQty',
      width: 100,
      align: 'right',
      render: (value: number, row) => `${formatQty(value)} ${row.unitName ?? ''}`
    },
    {
      title: '可用',
      dataIndex: 'availableQty',
      width: 100,
      align: 'right',
      render: (value: number) => (
        <Typography.Text type={value <= 0 ? 'danger' : undefined}>{formatQty(value)}</Typography.Text>
      )
    },
    {
      // 判源/预占口径是 available_qty - reserved_qty（StockReserveService），
      // 这里用同口径展示，避免「页面看有货、判源判不出」
      title: '可占用',
      key: 'freeQty',
      width: 110,
      align: 'right',
      render: (_value: unknown, row) => {
        const free = Math.max(0, (row.availableQty ?? 0) - (row.reservedQty ?? 0));
        return (
          <Tooltip title="可用量 − 预占量，等于判源时可被本次申领占用的数量">
            <Typography.Text type={free <= 0 ? 'danger' : undefined}>{formatQty(free)}</Typography.Text>
          </Tooltip>
        );
      }
    },
    { title: '占用', dataIndex: 'reservedQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '冻结', dataIndex: 'frozenQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '单位成本', dataIndex: 'unitCost', width: 120, align: 'right', render: (value: number) => <MoneyText value={value} precision={4} /> },
    { title: '金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => <MoneyText value={value} /> },
    {
      title: '效期',
      dataIndex: 'expiryDate',
      width: 130,
      render: (value: string | null) => {
        const date = parseApiTime(value ?? undefined);
        if (!date) return '-';
        const days = dayjs(date).diff(dayjs(), 'day');
        const text = formatDisplayDate(value);
        return days <= 90 ? (
          <Tooltip title={`${days} 天后到期`}>
            <Tag color="red">{text}</Tag>
          </Tooltip>
        ) : (
          text
        );
      }
    },
    { title: '闲置天数', dataIndex: 'idleDays', width: 100, align: 'right', render: (value: number) => (value > 180 ? <Tag color="orange">{value}</Tag> : value) },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Button type="link" onClick={() => navigate(`/stock/ledger?materialId=${row.materialId}`)}>
          流水
        </Button>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title="库存查询"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="物资编码 / 名称"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          onPressEnter={() => setSearch(keyword.trim())}
          style={{ width: 260 }}
        />
        <Select
          value={warehouseId}
          style={{ width: 190 }}
          showSearch
          optionFilterProp="label"
          onChange={setWarehouseId}
          options={[
            { value: '', label: '全部仓库' },
            ...(warehouses.data ?? []).map((row) => ({ value: row.id, label: `${row.whName}（${row.whCode}）` }))
          ]}
        />
        <Button type="primary" onClick={() => setSearch(keyword.trim())}>
          查询
        </Button>
        <Space size={6}>
          <Switch size="small" checked={onlyAvailable} onChange={setOnlyAvailable} />
          <Typography.Text type="secondary">只看有可用库存</Typography.Text>
        </Space>
      </Space>

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="库存加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<StockRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无库存记录' }}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : rows.length > 0 ? (
          <Typography.Text type="secondary">没有更多了</Typography.Text>
        ) : null}
      </Space>
    </Card>
  );
}
